import { camelCase } from 'change-case';
import isEqual from 'lodash/isEqual';

const REACT_ATTRIBUTES = {
  typeof: 'typeof'
};

const toReactProp = name => camelCase(REACT_ATTRIBUTES[name] || name);
const toSelector = (p, o, options = {}) =>
  `[${p}${options.loose ? '*' : ''}="${o.join(' ')}"]`;
const all = v => true; // eslint-disable-line no-unused-vars

export class Term {
  static defaultNamespace = false;
  static defaultName = 'term';

  constructor(options = {}) {
    const {
      name = this.constructor.defaultName,
      namespace = this.constructor.defaultNamespace
    } = options;
    this.name = name;
    this.namespace = namespace;
    this.equal = this.equal.bind(this);
    this.notEqual = this.notEqual.bind(this);
    this.equalType = this.equalType.bind(this);
    this.notEqualType = this.notEqualType.bind(this);
  }

  getName() {
    return this.namespace ? `${this.namespace}:${this.name}` : this.name;
  }

  toString() {
    return this.getName();
  }

  clone(...args) {
    const options = {
      name: this.name,
      namespace: this.namespace,
      ...(args.pop() || {})
    };
    return new this.constructor(...[...args, options]);
  }

  equalType(v) {
    return v instanceof this.constructor;
  }

  notEqualType(v) {
    return !this.equalType(v);
  }

  equal(v) {
    return this.equalType(v) && isEqual(v.toString(), this.toString());
  }

  notEqual(v) {
    return !this.equal(v);
  }

  static filter(options = {}) {
    const { o = all } = options;
    return o;
  }

  filter(options) {
    return this.constructor.filter(options);
  }
}

export class Predicate extends Term {
  static defaultName = 'predicate';

  objects = [];

  is(...objects) {
    this.objects = Array.from(new Set([...this.objects, ...objects]));
    return this;
  }

  toDouble(filter = all) {
    return [
      this.getName(),
      this.objects
        .slice()
        .filter(filter)
        .map(v => v.toString())
    ];
  }

  toSelector(filter, options = {}) {
    let fn = filter;
    let opt = options;

    if (typeof fn === 'object') {
      opt = fn;
      fn = undefined;
    }

    const { loose = false } = opt; // eslint-disable-line no-unused-vars

    const [p, o] = this.toDouble(fn);
    return toSelector(p, o, options);
  }

  toProps(filter) {
    const [p, o] = this.toDouble(filter);
    return {
      [toReactProp(p)]: o.join(' ')
    };
  }

  clone(...args) {
    const clone = super.clone(...args);
    clone.objects = this.objects.slice();
    return clone;
  }

  equal(v) {
    return super.equal(v) && isEqual(this.toDouble(), v.toDouble());
  }
}

export class Subject extends Term {
  static defaultName = 'subject';

  predicates = {};

  has(...predicates) {
    const notPredicate = predicates.find(p => !(p instanceof Predicate));

    if (notPredicate) {
      throw new TypeError(
        `Only predicate instances are allowed, not ${notPredicate}`
      );
    }

    this.predicates = predicates.reduce((predicates, p) => {
      const name = p.getName();
      const predicate = predicates[p.getName()];

      if (!predicate)
        return {
          ...predicates,
          [name]: p
        };

      predicate.is(...p.objects);

      return predicates;
    }, this.predicates);

    return this;
  }

  toTriple(filter = all) {
    const s = this.getName();
    const predicates = Object.keys(this.predicates).reduce((results, k) => {
      const predicate = this.predicates[k];
      const objects = predicate.objects.slice();
      const p = predicate.getName();
      const o = objects
        .filter(o => filter(this, predicate, o))
        .map(o => o.toString());

      if (!o.length) return results;

      results[p] = o;
      return results;
    }, {});

    return Object.keys(predicates).map(p => [s, p, predicates[p]]);
  }

  toSelector(filter, options = {}) {
    let fn = filter;
    let opt = options;

    if (typeof fn === 'object') {
      opt = fn;
      fn = undefined;
    }

    const { loose = false } = opt; // eslint-disable-line no-unused-vars

    return (
      this.toTriple(fn)
        // eslint-disable-next-line no-unused-vars
        .map(([s, p, o]) => toSelector(p, o, opt))
        .join('')
    );
  }

  toProps(filter) {
    return this.toTriple(filter).reduce(
      // eslint-disable-next-line no-unused-vars
      (props, [s, p, o]) => ({
        ...props,
        [toReactProp(p)]: o.join(' ')
      }),
      {}
    );
  }

  clone(...args) {
    const clone = super.clone(...args);
    clone.predicates = Object.keys(this.predicates).reduce(
      (o, k) => ({
        ...o,
        [k]: this.predicates[k]
      }),
      {}
    );
    return clone;
  }

  equal(s) {
    return super.equal(s) && isEqual(this.toTriple(), s.toTriple());
  }

  static filter(options = {}) {
    const { s = all, p = all, o = all } = options;
    return s === all && p === all && o === all
      ? all
      : (subject, predicate, object) => s(subject) && p(predicate) && o(object);
  }
}

export class StaticPredicate extends Predicate {
  constructor({ name, namespace, ...options } = {}) {
    super(options);
  }
}

export class Resource extends Subject {
  static defaultName = 'resource';

  constructor(name, options) {
    const nameType = typeof name;
    if (nameType !== 'string') {
      throw new TypeError(`expected a string, but received ${nameType}`);
    }
    super({ ...options, name });
    this.hasType(About, this);
  }

  hasType(P, ...values) {
    const predicate = new P();
    if (!(predicate instanceof Predicate)) {
      throw new TypeError(`expected a Predicate, but received ${typeof P}`);
    }
    return this.has(predicate.is(...values.filter(v => v !== undefined)));
  }

  getName() {
    return this.name;
  }

  property(...properties) {
    return this.hasType(Property, ...properties);
  }

  content(...content) {
    return this.hasType(Content, ...content);
  }

  typeOf(...types) {
    return this.hasType(TypeOf, ...types);
  }

  child(...resources) {
    return this.hasType(
      Relationship,
      ...resources.map(r => (r instanceof Resource ? r : new Resource(r)))
    );
  }

  parent(...resources) {
    return this.hasType(
      ReverseRelationship,
      ...resources.map(r => (r instanceof Resource ? r : new Resource(r)))
    );
  }

  clone(name, options) {
    return super.clone(name || this.name, options);
  }
}

export class ResourcePredicate extends StaticPredicate {
  is(...objects) {
    const notResource = objects.find(o => !(o instanceof Resource));

    if (notResource) {
      throw new TypeError(
        `Only Resource instances are allowed, not ${notResource}`
      );
    }

    return super.is(...objects);
  }
}

export class About extends ResourcePredicate {
  static defaultName = 'about';
}

export class TypeOf extends StaticPredicate {
  static defaultName = 'typeof';
}

export class Property extends StaticPredicate {
  static defaultName = 'property';
}

export class Content extends StaticPredicate {
  static defaultName = 'content';
}

export class Relationship extends ResourcePredicate {
  static defaultName = 'rel';
}

export class ReverseRelationship extends ResourcePredicate {
  static defaultName = 'rev';
}

export const filterByType = type => v => v instanceof type;
export const filterExclude = filter => v => !filter(v);
export const filterExcludeType = type => filterExclude(filterExcludeType(type));
export const filterBySubject = filter => (s, p, o) => filter(s); // eslint-disable-line no-unused-vars
export const filterByPredicate = filter => (s, p, o) => filter(p); // eslint-disable-line no-unused-vars
export const filterByObject = filter => (s, p, o) => filter(o);
export const filterByAny = (...filters) => (s, p, o) =>
  !!filters.find(filter => filter(s, p, o));

export const filterByResource = filterBySubject(filterByType(Resource));
export const filterExcludeResource = filterBySubject(
  filterExclude(filterByType(Resource))
);

export const filterByAbout = filterByPredicate(filterByType(About));
export const filterExcludeAbout = filterByPredicate(
  filterExclude(filterByType(About))
);

export const filterByTypeOf = filterByPredicate(filterByType(TypeOf));
export const filterExcludeTypeOf = filterByPredicate(
  filterExclude(filterByType(TypeOf))
);

export const filterByProperty = filterByPredicate(filterByType(Property));
export const filterExcludeProperty = filterByPredicate(
  filterExclude(filterByType(Property))
);

export const filterByContent = filterByPredicate(filterByType(Content));
export const filterExcludeContent = filterByPredicate(
  filterExclude(filterByType(Content))
);



// WEBPACK FOOTER //
// ./js/util/RDFTypes.js